import React, { useState } from "react";
import styles from "./PasswordSetup.module.scss";
import Input from "../Forms/Input/Input";
import Button from "../Forms/Button/Button";
import { motion } from "framer-motion";
import { fireAnalytics } from "../../../utils/firebase.js";
import { PasswordSetupProps } from "../../../types/register.ts";
import { Eye, EyeOff } from "lucide-react";
import PDF from "../../Footer/PDF/Terminos-y-condiciones-edify-2021.pdf";

const formVariant = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1,
    },
  },
};

const itemVariant = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const PasswordSetup: React.FC<PasswordSetupProps> = ({ 
  email,
  onPasswordSubmit, 
  isLoading, 
  errorMessage 
}) => {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [skipOnboarding, setSkipOnboarding] = useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);

  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError("La contraseña es obligatoria");
      return false;
    }
    
    if (password.length < 8) {
      setPasswordError("La contraseña debe tener al menos 8 caracteres entre letras y números");
      return false;
    }
    
    // Validar que contenga al menos una letra y un número
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    
    if (!hasLetter || !hasNumber) {
      setPasswordError("La contraseña debe contener letras y números");
      return false;
    }
    
    setPasswordError("");
    return true;
  };

  const validateConfirmPassword = (confirmPassword: string): boolean => {
    if (!confirmPassword) {
      setConfirmPasswordError("Confirma tu contraseña");
      return false;
    }
    
    if (confirmPassword !== password) {
      setConfirmPasswordError("Las contraseñas no coinciden");
      return false;
    }
    
    setConfirmPasswordError("");
    return true;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setPassword(value);
    
    if (passwordError) {
      setPasswordError("");
    }
    
    // Validar confirmación si ya hay un valor
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError("Las contraseñas no coinciden");
    } else if (confirmPassword && value === confirmPassword) {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    if (confirmPasswordError) {
      setConfirmPasswordError("");
    }
  };

  const handlePasswordBlur = (): void => {
    if (password) {
      validatePassword(password);
    }
  };

  const handleConfirmPasswordBlur = (): void => {
    if (confirmPassword) {
      validateConfirmPassword(confirmPassword);
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = (): void => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);
    
    if (!isPasswordValid || !isConfirmPasswordValid || !acceptTerms) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      fireAnalytics("registro_cuenta_creada", { category: "usuario" });
      await onPasswordSubmit({ 
        email, 
        password, 
        confirmPassword,
        skipOnboarding
      });
    } catch (error) {
      console.error("❌ Error en PasswordSetup handleSubmit:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      variants={formVariant}
      initial="hidden"
      animate="visible"
      className={styles.container}
    >
      <motion.div variants={itemVariant} className={styles.content}>
        <motion.p 
          variants={itemVariant}
          className={styles.subtitle}
        >
          Crea tu contraseña para completar el registro
        </motion.p>

        <motion.div variants={itemVariant} className={styles.emailDisplay}>
          <span className={styles.emailLabel}>Email validado:</span>
          <span className={styles.emailValue}>{email}</span>
        </motion.div>

        <motion.form variants={itemVariant} className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <Input
                field={{
                  type: showPassword ? "text" : "password",
                  placeholder: "Ingresa tu contraseña",
                  value: password,
                  onChange: handlePasswordChange,
                  onBlur: handlePasswordBlur
                }}
                label="Contraseña"
                error={passwordError}
                handleKey={() => {}}
              />
              <button
                type="button"
                className={styles.visibilityToggle}
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? (
                  <EyeOff size={20} className={styles.visibilityIcon} />
                ) : (
                  <Eye size={20} className={styles.visibilityIcon} />
                )}
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <Input
                field={{
                  type: showConfirmPassword ? "text" : "password",
                  placeholder: "Confirma tu contraseña",
                  value: confirmPassword,
                  onChange: handleConfirmPasswordChange,
                  onBlur: handleConfirmPasswordBlur
                }}
                label="Confirmar contraseña"
                error={confirmPasswordError}
                handleKey={() => {}}
              />
              <button
                type="button"
                className={styles.visibilityToggle}
                onClick={toggleConfirmPasswordVisibility}
                aria-label={showConfirmPassword ? "Ocultar confirmación" : "Mostrar confirmación"}
              >
                {showConfirmPassword ? (
                  <EyeOff size={20} className={styles.visibilityIcon} />
                ) : (
                  <Eye size={20} className={styles.visibilityIcon} />
                )}
              </button>
            </div>
          </div>

          {errorMessage && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={styles.errorMessage}
            >
              {errorMessage}
            </motion.div>
          )}

          <motion.div 
            variants={itemVariant}
            className={styles.checkboxesContainer}
          >
            <div className={styles.checkboxContainer}>
              <input 
                type="checkbox" 
                id="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className={styles.checkbox}
              />
              <label htmlFor="acceptTerms" className={styles.checkboxLabel}>
                <span className={styles.termsText}>
                  Acepto los{" "}
                  <a 
                    href={PDF} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.termsLink}
                  >
                    términos y condiciones
                  </a>
                </span>
              </label>
            </div>

            <div className={styles.checkboxContainer}>
              <input 
                type="checkbox" 
                id="skipOnboarding"
                checked={skipOnboarding}
                onChange={(e) => setSkipOnboarding(e.target.checked)}
                className={styles.checkbox}
              />
              <label htmlFor="skipOnboarding" className={styles.checkboxLabel}>
                <span className={styles.skipText}>
                  Completar mi perfil más tarde
                </span>
                <span className={styles.skipSubtext}>
                  (se asignarán datos básicos por defecto)
                </span>
              </label>
            </div>
          </motion.div>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading || !password || !confirmPassword || !!passwordError || !!confirmPasswordError || !acceptTerms}
            className={styles.btn}
            colored
            bordered={false}
            link={false}
            typebtn="primary"
          >
            {isLoading || isSubmitting ? "Creando cuenta..." : "Crear cuenta"}
          </Button>
        </motion.form>
      </motion.div>
    </motion.div>
  );
};

export default PasswordSetup;
